import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';

import { ECharts } from 'echarts';
import { AttributeData, Data } from 'src/app/model';

@Component({
  selector: 'app-attribute-line-chart',
  templateUrl: './attribute-line-chart.component.html',
  styleUrls: ['./attribute-line-chart.component.css']
})
export class AttributeLineChartComponent implements OnInit {

  @HostListener('window:resize', ['$event'])
  onResize() {
    setTimeout(() => {
      this._echartsInstance.resize();
    }, 1);
  }
  @Input('selectedAttribute')
  set selectedAttribute(value: { name: string, uom: string, isSelected: boolean }) {
    if (value) {
      this._selectedAttribute = value;
    }
  }
  @Input() attributeData: AttributeData;
  @Input() lineData: any;
  @Output() dataZoom = new EventEmitter<any>();

  chartOption: any;
  private _shapes: any[] = [];
  private _selectedAttribute: { name: string, uom: string, isSelected: boolean };
  private _initialData: AttributeData = {};
  private _echartsInstance: ECharts;
  private _combinedArray: [string, number][][];

  ngOnInit(): void {
    this._initialData = this.attributeData;
  }

  ngOnChanges() {
    if (this.lineData.length)
      this._plotLine();
  }

  /**
   * Determines whether chart init on
   * @param echartsInstance 
   */
  onChartInit(echartsInstance: ECharts): void {
    this._echartsInstance = echartsInstance;
    echartsInstance.on('restore', this.onRestore.bind(this));
  }

  /**
   * Determines whether restore on
   */
  onRestore(): void {
    this.dataZoom.emit(this._initialData);
  }

  /**
   * Determines whether data zoom on
   */
  onDataZoom(): void {
    const dataZoom = this._echartsInstance?.getOption()?.['dataZoom']?.[0];
    const startValue = new Date(dataZoom?.startValue)?.getTime();
    const endValue = new Date(dataZoom?.endValue)?.getTime();
    let values: Data[] = Object.values(this.attributeData)?.[0] as Data[];
    let visibleData: AttributeData = {};
    const filteredValues = values?.filter((point: Data) => {
      const pointTime = new Date(point?.timestamp)?.getTime();
      return pointTime >= startValue && pointTime <= endValue;
    });
    visibleData[this._selectedAttribute?.name] = filteredValues;
    this.dataZoom.emit(visibleData);
  }

  /**
   * Plots line
   */
  private _plotLine() {
    this._shapes = [];
    if (this.lineData?.find((item: any) => item.attributeThresholdMarker)?.attributeThresholdMarker) {
      let attributeThresholdMarker = this.lineData?.find((item: any) => item.attributeThresholdMarker)?.attributeThresholdMarker;
      if ((attributeThresholdMarker?.upper || attributeThresholdMarker?.upper === 0) && (attributeThresholdMarker?.showAttributeUpperLine || attributeThresholdMarker?.showWindowUpperLine)) {
        this._shapes.push({
          yAxis: attributeThresholdMarker?.upper,
          lineStyle: {
            color: attributeThresholdMarker?.color ? attributeThresholdMarker?.color : 'rgb(255, 0, 0)',
          }
        })
      }
      if ((attributeThresholdMarker?.lower || attributeThresholdMarker?.lower === 0) && (attributeThresholdMarker?.showAttributeLowerLine || attributeThresholdMarker?.showWindowLowerLine)) {
        this._shapes.push({
          yAxis: attributeThresholdMarker?.lower,
          lineStyle: {
            color: attributeThresholdMarker?.color ? attributeThresholdMarker?.color : 'rgb(255, 0, 0)',
          }
        })
      }
    }
    if (this.lineData?.find((item: any) => item.deviceThresholdMarker)?.deviceThresholdMarker) {
      let deviceThresholdMarker = this.lineData?.find((item: any) => item.deviceThresholdMarker)?.deviceThresholdMarker;
      if ((deviceThresholdMarker?.upper || deviceThresholdMarker?.upper === 0) && (deviceThresholdMarker?.deviceAttributeUpperLine || deviceThresholdMarker?.deviceWindowUpperLine)) {
        this._shapes.push({
          yAxis: deviceThresholdMarker?.upper,
          lineStyle: {
            color: deviceThresholdMarker?.color ? deviceThresholdMarker?.color : 'rgb(255, 0, 0)',
          }
        })
      }
      if ((deviceThresholdMarker?.lower || deviceThresholdMarker?.lower === 0) && (deviceThresholdMarker?.deviceAttributeLowerLine || deviceThresholdMarker?.deviceWindowLowerLine)) {
        this._shapes.push({
          yAxis: deviceThresholdMarker?.lower,
          lineStyle: {
            color: deviceThresholdMarker?.color ? deviceThresholdMarker?.color : 'rgb(255, 0, 0)',
          }
        })
      }
    }

    this._combinedArray = [];
    this.lineData.forEach((line) => {
      const combined = line?.x?.map((time, index) => {
        return [
          time,
          line.y[index]
        ];
      });
      this._combinedArray.push(combined);
    });

    const allYValues = this._combinedArray?.flat()?.filter(point => Array.isArray(point) && point.length > 1)?.map(point => point[1]);
    const yvaluesMax = Math.max(...[...allYValues]);
    const yvaluesMin = Math.min(...[...allYValues]);
    const uom = this._selectedAttribute?.uom;

    // Configuring ECharts options
    this.chartOption = {
      title: {
        text: this.lineData[0]?.title,
      },
      tooltip: {
        trigger: 'item',
        backgroundColor: '#EDEBEB',
        textStyle: {
          fontFamily: 'Inter',
          color: '#585A5A',
          fontSize: 12,
        },
        formatter: function (value) {
          var xAxisValue = value?.data[0];
          var yAxisValue = value?.data[1];
          var dateObject = new Date(xAxisValue);
          var formattedXAxis = `${dateObject.toLocaleDateString('en-US',
            { month: 'short', day: 'numeric', year: 'numeric' })}, ${dateObject.getHours()}:${('0' + dateObject.getMinutes()).slice(-2)}`;
          return `${formattedXAxis}<br /> ${yAxisValue} ${uom ?? ''}`;
        },
      },
      toolbox: {
        right: '10%',
        show: true,
        feature: {
          dataZoom: {
            show: true,
            icon: {
              back: ' '
            }
          },
          restore: {}
        }
      },
      legend: {
        show: this.lineData[0]?.legend ? this.lineData[0]?.legend : false,
        orient: 'horizontal',
        textStyle: {
          fontFamily: 'Inter',
          fontSize: 12,
        },
        bottom: 'bottom',
        left: 90
      },
      dataZoom: [
        {
          type: 'select',
          xAxisIndex: 0,
          start: 0,
          end: 100
        }
      ],
      xAxis: {
        type: 'time',
        boundaryGap: false,
        axisLabel: {
          formatter: function (value) {
            const date = new Date(value);
            const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
            const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
            return `${time}\n${formattedDate}`;
          },
          hideOverlap: true
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          rotate: 45,
          formatter: (value: number) => {
            return Math.floor(value)?.toString(); // Regular values
          },
          textStyle: {
            fontSize: 12,
            fontFamily: 'Arial'
          }
        },
        showGrid: this.lineData[0]?.showgrid,
        min: yvaluesMin,
        max: yvaluesMax
      },
      series: this._combinedArray.map((data, index) => ({
        type: 'line',
        data: data,
        name: this.lineData[index]?.name,
        markPoint: {
          symbolOffset: [50, '55%'],
          silent: true,
          data: this.lineData[index]?.annotations?.map(element => ({
            coord: [element.x, element.y],
            symbol: 'rect',
            symbolSize: [120, 30],
            label: {
              color: element?.font?.color,
              fontFamily: element?.font?.fontFamily,
              fontSize: element?.font?.fontSize,
              formatter: element?.text
            },
            itemStyle: {
              color: element?.bgcolor,
              opacity: element?.opacity
            },
          })),
        },
        markLine: {
          silent: true,
          lineStyle: {
            color: '#' + this._shapes?.[0]?.lineStyle?.color
          },
          data: this._shapes?.length
            ? [{ yAxis: this._shapes[0].yAxis }]
            : []
        },
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: {
          color: this.lineData[index]?.line?.color
        },
        itemStyle: {
          color: Array.isArray(this.lineData[index]?.marker?.color) && this.lineData[index]?.marker?.color?.length
            ? this.lineData[index]?.marker?.color?.[0]
            : this.lineData[index]?.marker?.color
        }
      })),
      backgroundColor: this.lineData[0]?.barColor ? this.lineData[0]?.barColor : "#f6f6f6",
    };
  }
}
